

/*
	Expressions are converted to reflect the full path before this function is called,
	The component path is also a full path.
	Locations are matched start-to-end. In regex terms: /^XXX$/.

	Currently supported syntax:
	['vertical 0',..,]		- a path through a component tree
	*						- one mandatory component (any name)
	**						- zero to many components
	^						- match top of the page
	'text:title'			- component with class filter
	'* foreground'			- any component with childKey 'foreground' (compound: name + childKey filter)
	'overlay foreground'	- overlay with childKey 'foreground'
	'*:class 0'				- class filter and childKey filter combined
	'* *'					- any component with any childKey
	'*:my\ class'			- class filter whose name contains a delimiter: ':', ' ' and '\' are each
							  backslash-escaped in the expression (see escapeStyleClass) and unescaped
							  when read back

	{location:['a','**','c'], matchChildren:false }	- return matches after a parent match has already been found
	{location:['a','**','c'], matchChildren:true }	- just return the one match on a branch

	Allow locations in themes to be nested - eg:
		styles: {
			'Test style': [{
				location: ['*'],
				css: {
					backgroundColor: '#b2b23375'
				},
				children: [{
					location: ['text:title'],
					css: {
						color: '#5cd5ff'
					}
				}]
			}]
		}

	ALTERNATIVE/SUPPLEMENTARY SYNTAXES AND APPROACHES
	1. CSS 
	2. JSONPath (see all the filters)
	3. solid.js set store path 
	4. Bash regex matching... + other regexs

	Note that using "location-style" matching rather than CSS has the advantage of 
	being simpler to implement and use and the big advantage of removing the need to use classes. 
	Additionally locations also allow us to change the HTML to render, not just the CSS.
*/

import { PathElement } from 'Shared/frontend/widgets/StylesProvider';
import { LocationPathStep } from 'Shared/model/venueSettings';

export function locationMatches(expression:(string|number)[],path:PathElement[])
{
	let i=0, j=0;

	while (true) {
		if (i == expression.length && j == path.length) 
			return true;
		else if (expression[i] == '**') 
			return someTailMatches(expression.slice(i+1),path,j);
		else if (i == expression.length || j == path.length)
			return false;
		else if (expressionMatches(expression[i],path[j])) {
			i++;
			j++;
		}
		else
			return false;
	}
}

/* There's likely to be a more efficient algorithm for this */
function someTailMatches(expressionTail:string[],path:PathElement[],j:number)
{
	if (expressionTail.length == 0 || path.length == 0)
		return true;

	for (let j2=j; j2<path.length; j2++) 
		if (locationMatches(expressionTail,path.slice(j2)))
			return true;

	return false;
}

function expressionMatches(expr:string|number,pathElement:PathElement)
{
	const str = typeof expr == 'number' ? ''+expr : expr;

	/* An unescaped space splits off a trailing childKey filter ('* foreground', '*:class 0'); an
	   unescaped colon then splits the component from a class filter. A class name carrying either
	   char, or a backslash, arrives here with them escaped. */
	const spaceIdx = firstUnescaped(str,' ');
	const componentPart = spaceIdx == -1 ? str : str.substring(0,spaceIdx);
	const childKeyFilter = spaceIdx == -1 ? undefined : str.substring(spaceIdx + 1);

	const colonIdx = firstUnescaped(componentPart,':');
	const component = colonIdx == -1 ? componentPart : componentPart.substring(0,colonIdx);
	const aClass = colonIdx == -1 ? undefined : unescapeStyleClass(componentPart.substring(colonIdx + 1));

	const componentOk = component == pathElement.key || component == '*';
	const classOk = !aClass || (pathElement.classes ?? []).includes(aClass);
	const childKeyOk = childKeyFilter == undefined || childKeyFilter == '*'
		|| childKeyFilter == String(pathElement.childKey);

	return componentOk && classOk && childKeyOk;
}

/*
	A class name as it sits inside a location expression, with the grammar's delimiters - ':', ' '
	and the escape char '\' itself - backslash-escaped. ruleLocation() and selectLocation() run every
	Style Builder / Selector class through this; expressionMatches() reverses it.
*/
export function escapeStyleClass(name:string): string
{
	return name.replace(/[\\: ]/g,'\\$&');
}

/*
	The location expression a path built in LocationBuilder stands for - see locationPathStep in
	Shared/model/venueSettings.ts.

	Expressions are matched against the path from the styled component down, and the leading '*' is
	that component: a step is what comes after it. A child step is one component ('*', or '*:class'
	when it names one); a descendant step is '**' first, which soaks up any number of components in
	between. So an empty path is ['*'] - the styled component itself, which is where a path that has
	had no steps added yet should apply.
*/
export function pathLocation(steps:LocationPathStep[]): string[]
{
	const ret = ['*'];

	for (const step of steps) {
		if (step.relation == 'descendant')
			ret.push('**');
		if (step.aClass != '')
			ret.push(`*:${escapeStyleClass(step.aClass)}`);
		else if (step.relation == 'child')
			ret.push('*');
	}

	return ret;
}

function unescapeStyleClass(token:string): string
{
	return token.replace(/\\(.)/g,'$1');
}

/* The index of the first unescaped occurrence of ch in str, or -1. A backslash consumes the char
   after it, so '\ ' is skipped over when looking for a bare ' '. */
function firstUnescaped(str:string,ch:string): number
{
	for (let i=0; i<str.length; i++) {
		if (str[i] == '\\') {
			i++;
			continue;
		}
		if (str[i] == ch)
			return i;
	}
	return -1;
}

